import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Context } from '../../../App';
import { svgContainerToPngBase64 } from '../../../helpers/svgContainerToPngBase64';
import buildSlopeStabilitySketchModel from './slopeStabilitySketchModel';

const formatTick = (value) => {
  if (!Number.isFinite(value)) return '-';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const LIGHT_COLORBLIND_PALETTE_STOPS = ['#440154', '#414487', '#2A788E', '#22A884', '#7AD151', '#FDE725'];
const DARK_COLORBLIND_PALETTE_STOPS = ['#6C3FB2', '#5A6FE3', '#37A3B3', '#3EC38C', '#A8DC62', '#FFE45E'];

const hexToRgb = (hex) => {
  const normalized = String(hex || '').trim().replace('#', '');
  if (normalized.length !== 6) return null;
  const intValue = Number.parseInt(normalized, 16);
  if (!Number.isFinite(intValue)) return null;
  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255,
  };
};

const interpolateRgb = (a, b, t) => ({
  r: Math.round(a.r + (b.r - a.r) * t),
  g: Math.round(a.g + (b.g - a.g) * t),
  b: Math.round(a.b + (b.b - a.b) * t),
});

const rgbToCss = (rgb) => `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;

const colorFromStops = (stops, t) => {
  if (!Array.isArray(stops) || stops.length < 2) return '#f57c00';
  const clamped = clamp(t, 0, 1);
  const scaled = clamped * (stops.length - 1);
  const indexLow = Math.floor(scaled);
  const indexHigh = Math.min(stops.length - 1, indexLow + 1);
  const localT = scaled - indexLow;
  const low = hexToRgb(stops[indexLow]);
  const high = hexToRgb(stops[indexHigh]);
  if (!low || !high) return stops[indexLow] || '#f57c00';
  return rgbToCss(interpolateRgb(low, high, localT));
};

const fsToHeatColor = (value, minFs, maxFs, palette = 'default', theme = 'light', emptyColor = '#c8c8c8') => {
  if (!Number.isFinite(value)) return emptyColor;
  if (!Number.isFinite(minFs) || !Number.isFinite(maxFs) || maxFs <= minFs) return '#f57c00';
  const t = clamp((value - minFs) / (maxFs - minFs), 0, 1);
  if (palette === 'colorblind') {
    return colorFromStops(theme === 'dark' ? DARK_COLORBLIND_PALETTE_STOPS : LIGHT_COLORBLIND_PALETTE_STOPS, t);
  }
  const hue = 240 * t;
  return `hsl(${hue}, 96%, 50%)`;
};

const formatLegendFs = (value) => (Number.isFinite(value) ? value.toFixed(3) : '-');
const SVG_FONT_FAMILY = "'Lato', sans-serif";
const normalizeFailureDirection = (value) => {
  if (value === 'left_to_right') return 'left_to_right';
  if (value === 'right_to_left') return 'right_to_left';
  return 'auto';
};

const buildSketchColors = (theme) =>
  theme === 'dark'
    ? {
        canvasBackground: '#0f151b',
        plotBackground: '#151d26',
        plotBorder: '#33414f',
        gridY: '#25303a',
        gridX: '#202933',
        axis: '#6f7d89',
        soilFill: '#d0ab82',
        soilFillOpacity: 0.24,
        profileStroke: '#d7b89e',
        profileMarker: '#e2cdbd',
        profileLabel: '#e2cdbd',
        water: '#74c0fc',
        slopeLimit: '#58d0b7',
        singleSurface: '#ff8a80',
        searchOutline: '#ffb74d',
        searchTick: '#ffa726',
        searchLabel: '#ffd180',
        heatCellFallback: '#45515d',
        heatCellOpacity: 0.46,
        heatCellHoverOpacity: 0.6,
        legendStroke: '#8fa1ae',
        legendText: '#c0ccd5',
        hoverSurface: '#7cb7ff',
        pinnedSurface: '#ffcf85',
        criticalSurface: '#7fe08b',
        tickText: '#aab7c1',
        coord: '#9fb0ba',
        modeSingle: '#ff8a80',
        modeSearch: '#7cb7ff',
        directionBadge: '#b0bec5',
        downloadButtonFill: '#1e2730',
        downloadButtonStroke: '#4b6070',
        downloadButtonIcon: '#dfe8ef',
        badgeFillOpacity: 0.18,
        directionBadgeFillOpacity: 0.16,
        emptyText: '#97a5b1',
        warningText: '#aebbc6',
      }
    : {
        canvasBackground: '#fbfbfb',
        plotBackground: '#ffffff',
        plotBorder: '#d9d9d9',
        gridY: '#efefef',
        gridX: '#f2f2f2',
        axis: '#c0c0c0',
        soilFill: '#d1b285',
        soilFillOpacity: 0.25,
        profileStroke: '#6d4c41',
        profileMarker: '#5d4037',
        profileLabel: '#5d4037',
        water: '#1976d2',
        slopeLimit: '#00695c',
        singleSurface: '#c62828',
        searchOutline: '#d97706',
        searchTick: '#b45309',
        searchLabel: '#b45309',
        heatCellFallback: '#c8c8c8',
        heatCellOpacity: 0.36,
        heatCellHoverOpacity: 0.5,
        legendStroke: '#666666',
        legendText: '#555555',
        hoverSurface: '#1565c0',
        pinnedSurface: '#ef6c00',
        criticalSurface: '#2e7d32',
        tickText: '#666666',
        coord: '#666666',
        modeSingle: '#c62828',
        modeSearch: '#1565c0',
        directionBadge: '#455a64',
        downloadButtonFill: '#ffffff',
        downloadButtonStroke: '#c8d2db',
        downloadButtonIcon: '#455a64',
        badgeFillOpacity: 0.14,
        directionBadgeFillOpacity: 0.12,
        emptyText: '#8a8a8a',
        warningText: undefined,
      };

const SlopeStabilitySketch = ({ input, mode, criticalSurface, searchHeatmap, heatmapPalette = 'default' }) => {
  const { theme: appTheme } = useContext(Context);
  const colors = useMemo(() => buildSketchColors(appTheme), [appTheme]);
  const sketchContainerRef = useRef(null);
  const model = useMemo(
    () => buildSlopeStabilitySketchModel({ input, mode, criticalSurface, searchHeatmap }),
    [criticalSurface, input, mode, searchHeatmap]
  );
  const [hoveredHeatCellIndex, setHoveredHeatCellIndex] = useState(null);
  const [pinnedHeatCellIndex, setPinnedHeatCellIndex] = useState(null);
  useEffect(() => {
    setHoveredHeatCellIndex(null);
    setPinnedHeatCellIndex(null);
  }, [mode, model.heatmap.enabled, model.heatmap.cells]);
  const hoveredHeatCell =
    Number.isInteger(hoveredHeatCellIndex) && hoveredHeatCellIndex >= 0 && hoveredHeatCellIndex < model.heatmap.cells.length
      ? model.heatmap.cells[hoveredHeatCellIndex]
      : null;
  const hoveredSurface = hoveredHeatCell?.surface || null;
  const pinnedHeatCell =
    Number.isInteger(pinnedHeatCellIndex) && pinnedHeatCellIndex >= 0 && pinnedHeatCellIndex < model.heatmap.cells.length
      ? model.heatmap.cells[pinnedHeatCellIndex]
      : null;
  const pinnedSurface = pinnedHeatCell?.surface || null;

  const modeBadgeWidth = Math.max(130, model.labels.mode.length * 6.6 + 18);
  const modeBadgeHeight = 24;
  const modeBadgeX = model.plot.right - modeBadgeWidth - 8;
  const modeBadgeY = model.plot.top + 8;
  const modeBadgeColor = mode === 'single' ? colors.modeSingle : colors.modeSearch;
  const failureDirection = normalizeFailureDirection(input?.surfaceFilters?.failureDirection);
  const directionBadgeLabel =
    failureDirection === 'left_to_right'
      ? 'Failure Direction: ➡'
      : failureDirection === 'right_to_left'
        ? 'Failure Direction: ⬅'
        : 'Failure Direction: ⇆';
  const directionBadgeWidth = modeBadgeWidth;
  const directionBadgeHeight = modeBadgeHeight;
  const directionBadgeX = modeBadgeX;
  const directionBadgeY = modeBadgeY + modeBadgeHeight + 6;
  const directionBadgeColor = colors.directionBadge;
  const coordOriginX = model.plot.left + 14;
  const coordOriginY = model.plot.bottom - 14;
  const coordLen = 24;
  const coordColor = colors.coord;
  const searchTickLength = 5;
  const slopeLimitColor = colors.slopeLimit;
  const heatLegend = {
    x: model.plot.left + 16,
    y: model.plot.top + 20,
    width: 10,
    height: 96,
    steps: 10,
  };
  const heatLegendStepHeight = heatLegend.height / heatLegend.steps;
  const downloadButtonSize = 28;
  const downloadButton = {
    x: model.plot.right - downloadButtonSize - 8,
    y: directionBadgeY + directionBadgeHeight + 8,
    size: downloadButtonSize,
    radius: 6,
  };
  const handleDownloadSketch = async () => {
    try {
      if (document.fonts?.ready) {
        try {
          await document.fonts.ready;
        } catch (err) {
          // Best effort: continue if font readiness check fails.
        }
      }

      const exportContainer = document.createElement('div');
      const exportSvg = sketchContainerRef.current?.querySelector('svg')?.cloneNode(true);
      if (!exportSvg) return;

      exportSvg.querySelectorAll('[data-export-exclude="true"]').forEach((node) => node.remove());
      exportContainer.appendChild(exportSvg);

      const pngBase64 = await svgContainerToPngBase64(exportContainer, {
        scale: 4,
        inlineImages: false,
      });
      if (!pngBase64) return;

      const anchor = document.createElement('a');
      anchor.href = `data:image/png;base64,${pngBase64}`;
      anchor.download = 'geocalc-slope-stability-sketch.png';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    } catch (err) {
      console.error(err);
    }
  };
  const handleDownloadButtonKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDownloadSketch();
    }
  };
  const handleHeatCellClick = (cellIndex, cell) => {
    if (!cell?.surface) return;
    setPinnedHeatCellIndex((currentIndex) => (currentIndex === cellIndex ? null : cellIndex));
  };

  return (
    <Stack spacing={1}>
      <Box
        ref={sketchContainerRef}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          px: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            minWidth: 0,
            '& svg': {
              display: 'block',
              width: '100%',
              height: 'auto',
              maxWidth: '100%',
            },
          }}
        >
          <svg
            viewBox={`0 0 ${model.frame.viewWidth} ${model.frame.viewHeight}`}
            role="img"
            aria-label="Slope stability input sketch"
            style={{ borderRadius: 6, background: colors.canvasBackground, fontFamily: SVG_FONT_FAMILY }}
          >
            <defs>
              <style>
                {`
                  .slope-sketch-root text {
                    font-family: ${SVG_FONT_FAMILY};
                  }
                  .download-sketch-button rect,
                  .download-sketch-button path {
                    transition: opacity 140ms ease-out;
                  }
                  .download-sketch-button:hover rect,
                  .download-sketch-button:focus rect,
                  .download-sketch-button:hover path,
                  .download-sketch-button:focus path {
                    opacity: 0.84;
                  }
                  .hover-surface-group {
                    animation: hoverSurfaceFadeIn 140ms ease-out;
                  }
                  .status-badge-group {
                    animation: statusBadgeFadeIn 180ms ease-out;
                  }
                  @keyframes hoverSurfaceFadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                  }
                  @keyframes statusBadgeFadeIn {
                    from { opacity: 0.7; }
                    to { opacity: 1; }
                  }
                `}
              </style>
            </defs>
            <g className="slope-sketch-root">
            <rect
              x={model.plot.left}
              y={model.plot.top}
              width={model.plot.width}
              height={model.plot.height}
              fill={colors.plotBackground}
              stroke={colors.plotBorder}
            />
            {model.ticks.y.map((tick) => (
              <line
                key={`grid-y-${tick.value}`}
                x1={model.plot.left}
                y1={tick.y}
                x2={model.plot.right}
                y2={tick.y}
                stroke={colors.gridY}
                strokeWidth="1"
              />
            ))}
            {model.ticks.x.map((tick) => (
              <line
                key={`grid-x-${tick.value}`}
                x1={tick.x}
                y1={model.plot.top}
                x2={tick.x}
                y2={model.plot.bottom}
                stroke={colors.gridX}
                strokeWidth="1"
              />
            ))}

            {Number.isFinite(model.axes.xAxisY) && (
              <line
                x1={model.plot.left}
                y1={model.axes.xAxisY}
                x2={model.plot.right}
                y2={model.axes.xAxisY}
                stroke={colors.axis}
                strokeWidth="1.1"
              />
            )}
            {Number.isFinite(model.axes.yAxisX) && (
              <line
                x1={model.axes.yAxisX}
                y1={model.plot.top}
                x2={model.axes.yAxisX}
                y2={model.plot.bottom}
                stroke={colors.axis}
                strokeWidth="1.1"
              />
            )}

            {model.hasRenderableProfile && (
              <>
                <polygon points={model.soilPolygon} fill={colors.soilFill} fillOpacity={colors.soilFillOpacity} />
                <polyline points={model.profilePolyline} fill="none" stroke={colors.profileStroke} strokeWidth="2.2" />
              </>
            )}

            {model.profilePx.map((point, idx) => (
              <g key={`profile-pt-${idx + 1}`}>
                <circle cx={point.x} cy={point.y} r="3" fill={colors.profileMarker} />
                <text x={point.x + 6} y={point.y - 6} fill={colors.profileLabel} fontSize="11" fontWeight="700">
                  P{idx + 1}
                </text>
              </g>
            ))}

            {model.water.visible && (
              <>
                <line
                  x1={model.plot.left}
                  y1={model.water.y}
                  x2={model.plot.right}
                  y2={model.water.y}
                  stroke={colors.water}
                  strokeWidth="1.8"
                  strokeDasharray="9 5"
                />
                <text x={model.plot.right - 8} y={model.water.y - 6} fill={colors.water} fontSize="11" fontWeight="700" textAnchor="end">
                  WT Elev. {formatTick(model.water.elevation)}
                </text>
              </>
            )}

            {model.slopeLimits.visible && (
              <g pointerEvents="none">
                {model.slopeLimits.left && (
                  <>
                    <line
                      x1={model.slopeLimits.left.x}
                      y1={model.plot.top}
                      x2={model.slopeLimits.left.x}
                      y2={model.plot.bottom}
                      stroke={slopeLimitColor}
                      strokeWidth="1.4"
                      strokeDasharray="6 4"
                    />
                    <text
                      x={model.slopeLimits.left.x + 4}
                      y={model.plot.top + 12}
                      fill={slopeLimitColor}
                      fontSize="10"
                      fontWeight="700"
                    >
                      {`L ${formatTick(model.slopeLimits.left.value)}`}
                    </text>
                  </>
                )}
                {model.slopeLimits.right && (
                  <>
                    <line
                      x1={model.slopeLimits.right.x}
                      y1={model.plot.top}
                      x2={model.slopeLimits.right.x}
                      y2={model.plot.bottom}
                      stroke={slopeLimitColor}
                      strokeWidth="1.4"
                      strokeDasharray="6 4"
                    />
                    <text
                      x={model.slopeLimits.right.x - 4}
                      y={model.plot.top + 12}
                      fill={slopeLimitColor}
                      fontSize="10"
                      fontWeight="700"
                      textAnchor="end"
                    >
                      {`R ${formatTick(model.slopeLimits.right.value)}`}
                    </text>
                  </>
                )}
              </g>
            )}

            {mode === 'single' && model.single && !model.critical && (
              <>
                <circle
                  cx={model.single.cx}
                  cy={model.single.cy}
                  r={model.single.radius}
                  fill="none"
                  stroke={colors.singleSurface}
                  strokeWidth="2"
                />
                <circle cx={model.single.cx} cy={model.single.cy} r="3" fill={colors.singleSurface} />
                <text x={model.single.cx + 8} y={model.single.cy - 8} fill={colors.singleSurface} fontSize="11" fontWeight="700">
                  {model.single.centerLabel}
                </text>
                <text x={model.single.cx + 8} y={model.single.cy + 10} fill={colors.singleSurface} fontSize="11" fontWeight="700">
                  {model.single.radiusLabel}
                </text>
              </>
            )}

            {mode === 'search' && model.search && (
              <>
                {model.heatmap.enabled &&
                  model.heatmap.cells.map((cell, idx) => (
                    <rect
                      key={`heat-cell-${idx + 1}`}
                      x={cell.x}
                      y={cell.y}
                      width={cell.width}
                      height={cell.height}
                      fill={fsToHeatColor(
                        cell.fs,
                        model.heatmap.minFs,
                        model.heatmap.capFs,
                        heatmapPalette,
                        appTheme,
                        colors.heatCellFallback,
                      )}
                      fillOpacity={idx === hoveredHeatCellIndex ? colors.heatCellHoverOpacity : colors.heatCellOpacity}
                      stroke="none"
                      style={{
                        cursor: cell.surface ? 'pointer' : 'default',
                        transition: 'fill-opacity 140ms ease-out, fill 180ms linear',
                      }}
                      onMouseEnter={() => setHoveredHeatCellIndex(cell.surface ? idx : null)}
                      onMouseLeave={() => setHoveredHeatCellIndex(null)}
                      onClick={() => handleHeatCellClick(idx, cell)}
                    />
                  ))}
                <rect
                  x={model.search.left}
                  y={model.search.top}
                  width={model.search.right - model.search.left}
                  height={model.search.bottom - model.search.top}
                  fill="none"
                  stroke={colors.searchOutline}
                  strokeWidth="1.8"
                />
                {model.search.centerXTicks.map((x, idx) => (
                  <g key={`search-x-tick-${idx + 1}`}>
                    <line
                      x1={x}
                      y1={model.search.top}
                      x2={x}
                      y2={model.search.top - searchTickLength}
                      stroke={colors.searchTick}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={x}
                      y1={model.search.bottom}
                      x2={x}
                      y2={model.search.bottom + searchTickLength}
                      stroke={colors.searchTick}
                      strokeWidth="1.2"
                    />
                  </g>
                ))}
                {model.search.centerYTicks.map((y, idx) => (
                  <g key={`search-y-tick-${idx + 1}`}>
                    <line
                      x1={model.search.left}
                      y1={y}
                      x2={model.search.left - searchTickLength}
                      y2={y}
                      stroke={colors.searchTick}
                      strokeWidth="1.2"
                    />
                    <line
                      x1={model.search.right}
                      y1={y}
                      x2={model.search.right + searchTickLength}
                      y2={y}
                      stroke={colors.searchTick}
                      strokeWidth="1.2"
                    />
                  </g>
                ))}
                <text x={model.search.left + 6} y={model.search.top - 8} fill={colors.searchLabel} fontSize="11" fontWeight="700">
                  Search center range
                </text>
                {model.heatmap.enabled && (
                  <g>
                    {Array.from({ length: heatLegend.steps }, (_, idx) => {
                      const ratio = idx / Math.max(1, heatLegend.steps - 1);
                      const value = model.heatmap.minFs + ratio * (model.heatmap.capFs - model.heatmap.minFs);
                      return (
                        <rect
                          key={`heat-legend-step-${idx + 1}`}
                          x={heatLegend.x}
                          y={heatLegend.y + idx * heatLegendStepHeight}
                          width={heatLegend.width}
                          height={heatLegendStepHeight + 0.2}
                          fill={fsToHeatColor(
                            value,
                            model.heatmap.minFs,
                            model.heatmap.capFs,
                            heatmapPalette,
                            appTheme,
                            colors.heatCellFallback,
                          )}
                          stroke="none"
                        />
                      );
                    })}
                    <rect
                      x={heatLegend.x}
                      y={heatLegend.y}
                      width={heatLegend.width}
                      height={heatLegend.height}
                      fill="none"
                      stroke={colors.legendStroke}
                      strokeWidth="0.7"
                    />
                    <text
                      x={heatLegend.x + heatLegend.width / 2}
                      y={heatLegend.y - 4}
                      fill={colors.legendText}
                      fontSize="9"
                      textAnchor="middle"
                    >
                      {heatmapPalette === 'colorblind' ? 'FS (CB)' : 'FS'}
                    </text>
                    <text x={heatLegend.x + heatLegend.width + 4} y={heatLegend.y + 8} fill={colors.legendText} fontSize="9">
                      {formatLegendFs(model.heatmap.minFs)}
                    </text>
                    <text
                      x={heatLegend.x + heatLegend.width + 4}
                      y={heatLegend.y + heatLegend.height}
                      fill={colors.legendText}
                      fontSize="9"
                      dominantBaseline="ideographic"
                    >
                      {model.heatmap.isCapped ? `${formatLegendFs(model.heatmap.capFs)}+` : formatLegendFs(model.heatmap.capFs)}
                    </text>
                  </g>
                )}
              </>
            )}

            {mode === 'search' && pinnedSurface && (
              <g key={`pinned-surface-${pinnedHeatCellIndex ?? 'none'}`} pointerEvents="none">
                {!pinnedSurface.showFullCircle && <path d={pinnedSurface.arcPath} fill="none" stroke={colors.pinnedSurface} strokeWidth="2" />}
                {pinnedSurface.radiusLines.map((line, idx) => (
                  <line
                    key={`pinned-radius-line-${idx + 1}`}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={colors.pinnedSurface}
                    strokeWidth="1.2"
                    strokeDasharray="5 3"
                  />
                ))}
                {pinnedSurface.leftIntersection && (
                  <circle cx={pinnedSurface.leftIntersection.x} cy={pinnedSurface.leftIntersection.y} r="2.4" fill={colors.pinnedSurface} />
                )}
                {pinnedSurface.rightIntersection && (
                  <circle cx={pinnedSurface.rightIntersection.x} cy={pinnedSurface.rightIntersection.y} r="2.4" fill={colors.pinnedSurface} />
                )}
                <circle cx={pinnedSurface.cx} cy={pinnedSurface.cy} r="2.7" fill={colors.pinnedSurface} />
                <text
                  x={pinnedSurface.fsX}
                  y={pinnedSurface.fsY}
                  fill={colors.pinnedSurface}
                  fontSize="10"
                  fontWeight="700"
                  textAnchor={pinnedSurface.fsAnchor}
                >
                  {pinnedSurface.fsLabel}
                </text>
              </g>
            )}

            {mode === 'search' && hoveredSurface && hoveredHeatCellIndex !== pinnedHeatCellIndex && (
              <g key={`hover-surface-${hoveredHeatCellIndex ?? 'none'}`} className="hover-surface-group" pointerEvents="none">
                {!hoveredSurface.showFullCircle && (
                  <path d={hoveredSurface.arcPath} fill="none" stroke={colors.hoverSurface} strokeWidth="1.8" />
                )}
                {hoveredSurface.radiusLines.map((line, idx) => (
                  <line
                    key={`hover-radius-line-${idx + 1}`}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={colors.hoverSurface}
                    strokeWidth="1.1"
                  />
                ))}
                {hoveredSurface.leftIntersection && (
                  <circle cx={hoveredSurface.leftIntersection.x} cy={hoveredSurface.leftIntersection.y} r="2.2" fill={colors.hoverSurface} />
                )}
                {hoveredSurface.rightIntersection && (
                  <circle cx={hoveredSurface.rightIntersection.x} cy={hoveredSurface.rightIntersection.y} r="2.2" fill={colors.hoverSurface} />
                )}
                <circle cx={hoveredSurface.cx} cy={hoveredSurface.cy} r="2.5" fill={colors.hoverSurface} />
                <text
                  x={hoveredSurface.fsX}
                  y={hoveredSurface.fsY}
                  fill={colors.hoverSurface}
                  fontSize="10"
                  fontWeight="700"
                  textAnchor={hoveredSurface.fsAnchor}
                >
                  {hoveredSurface.fsLabel}
                </text>
              </g>
            )}

            {model.critical && (
              <g pointerEvents="none">
                {model.critical.showFullCircle ? (
                  <circle
                    cx={model.critical.cx}
                    cy={model.critical.cy}
                    r={model.critical.radius}
                    fill="none"
                    stroke={colors.criticalSurface}
                    strokeWidth="2.2"
                  />
                ) : (
                  <>
                    <path d={model.critical.arcPath} fill="none" stroke={colors.criticalSurface} strokeWidth="2.2" />
                    {model.critical.radiusLines.map((line, idx) => (
                      <line
                        key={`critical-radius-line-${idx + 1}`}
                        x1={line.x1}
                        y1={line.y1}
                        x2={line.x2}
                        y2={line.y2}
                        stroke={colors.criticalSurface}
                        strokeWidth="1.2"
                      />
                    ))}
                    {model.critical.leftIntersection && (
                      <circle cx={model.critical.leftIntersection.x} cy={model.critical.leftIntersection.y} r="2.8" fill={colors.criticalSurface} />
                    )}
                    {model.critical.rightIntersection && (
                      <circle cx={model.critical.rightIntersection.x} cy={model.critical.rightIntersection.y} r="2.8" fill={colors.criticalSurface} />
                    )}
                  </>
                )}
                <circle cx={model.critical.cx} cy={model.critical.cy} r="3" fill={colors.criticalSurface} />
                <text
                  x={model.critical.fsX}
                  y={model.critical.fsY}
                  fill={colors.criticalSurface}
                  fontSize="11"
                  fontWeight="700"
                  textAnchor={model.critical.fsAnchor}
                >
                  {model.critical.fsLabel}
                </text>
              </g>
            )}

            {model.ticks.x.map((tick) => (
              <text key={`x-tick-${tick.value}`} x={tick.x} y={model.plot.bottom + 18} fill={colors.tickText} fontSize="10" textAnchor="middle">
                {formatTick(tick.value)}
              </text>
            ))}
            {model.ticks.y.map((tick) => (
              <text key={`y-tick-${tick.value}`} x={model.plot.left - 8} y={tick.y + 3} fill={colors.tickText} fontSize="10" textAnchor="end">
                {formatTick(tick.value)}
              </text>
            ))}
            <g>
              <line x1={coordOriginX} y1={coordOriginY} x2={coordOriginX + coordLen} y2={coordOriginY} stroke={coordColor} strokeWidth="1.2" />
              <polygon
                points={`${coordOriginX + coordLen},${coordOriginY} ${coordOriginX + coordLen - 5},${coordOriginY - 3} ${
                  coordOriginX + coordLen - 5
                },${coordOriginY + 3}`}
                fill={coordColor}
              />
              <line x1={coordOriginX} y1={coordOriginY} x2={coordOriginX} y2={coordOriginY - coordLen} stroke={coordColor} strokeWidth="1.2" />
              <polygon
                points={`${coordOriginX},${coordOriginY - coordLen} ${coordOriginX - 3},${coordOriginY - coordLen + 5} ${
                  coordOriginX + 3
                },${coordOriginY - coordLen + 5}`}
                fill={coordColor}
              />
              <text x={coordOriginX + coordLen + 6} y={coordOriginY + 3} fill={coordColor} fontSize="9" fontWeight="700">
                x
              </text>
              <text x={coordOriginX - 3} y={coordOriginY - coordLen - 6} fill={coordColor} fontSize="9" fontWeight="700">
                y
              </text>
            </g>
            <g key={`mode-badge-${model.labels.mode}-${mode}`} className="status-badge-group">
              <rect
                x={modeBadgeX}
                y={modeBadgeY}
                width={modeBadgeWidth}
                height={modeBadgeHeight}
                rx="12"
                fill={modeBadgeColor}
                fillOpacity={colors.badgeFillOpacity}
                stroke={modeBadgeColor}
                strokeWidth="1"
              />
              <text
                x={modeBadgeX + modeBadgeWidth / 2}
                y={modeBadgeY + modeBadgeHeight / 2 + 4}
                fill={modeBadgeColor}
                fontSize="11"
                fontWeight="700"
                textAnchor="middle"
              >
                {model.labels.mode}
              </text>
            </g>
            <g key={`direction-badge-${directionBadgeLabel}`} className="status-badge-group">
              <rect
                x={directionBadgeX}
                y={directionBadgeY}
                width={directionBadgeWidth}
                height={directionBadgeHeight}
                rx="10"
                fill={directionBadgeColor}
                fillOpacity={colors.directionBadgeFillOpacity}
                stroke={directionBadgeColor}
                strokeWidth="1"
              />
              <text
                x={directionBadgeX + directionBadgeWidth / 2}
                y={directionBadgeY + directionBadgeHeight / 2 + 4}
                fill={directionBadgeColor}
                fontSize="11"
                fontWeight="800"
                textAnchor="middle"
              >
                {directionBadgeLabel}
              </text>
            </g>
            <g
              className="download-sketch-button"
              data-export-exclude="true"
              transform={`translate(${downloadButton.x} ${downloadButton.y})`}
              onClick={handleDownloadSketch}
              onKeyDown={handleDownloadButtonKeyDown}
              role="button"
              tabIndex={0}
              aria-label="Download sketch as PNG"
              style={{ cursor: 'pointer', outline: 'none' }}
            >
              <title>Download sketch as PNG</title>
              <rect
                x="0"
                y="0"
                width={downloadButton.size}
                height={downloadButton.size}
                rx={downloadButton.radius}
                fill={colors.downloadButtonFill}
                stroke={colors.downloadButtonStroke}
              />
              <path d="M14 7.5V16" fill="none" stroke={colors.downloadButtonIcon} strokeWidth="1.8" strokeLinecap="round" />
              <path
                d="M10.2 12.4L14 16.2L17.8 12.4"
                fill="none"
                stroke={colors.downloadButtonIcon}
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M9.5 20.2H18.5" fill="none" stroke={colors.downloadButtonIcon} strokeWidth="1.8" strokeLinecap="round" />
            </g>

            {!model.hasRenderableProfile && (
              <text
                x={model.plot.left + model.plot.width / 2}
                y={model.plot.top + model.plot.height / 2}
                fill={colors.emptyText}
                fontSize="13"
                textAnchor="middle"
              >
                Add at least two valid ground profile points to preview the slope.
              </text>
            )}
            </g>
          </svg>
        </Box>
      </Box>
      {model.warnings.map((warning) => (
        <Typography key={warning} variant="caption" color="text.secondary" sx={colors.warningText ? { color: colors.warningText } : undefined}>
          {warning}
        </Typography>
      ))}
    </Stack>
  );
};

export default React.memo(SlopeStabilitySketch);
